import React, { useState, useRef, useEffect } from 'react';
import { Paperclip, FileText, Trash2, Loader2, X } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Tag } from 'react-tag-autocomplete';
import { emailAddressSearchService } from '@/services/emailSearch';
import { useDebounce } from '@/hooks/useDebounce';
import { useCloudbridgeQuillImages } from '@/hooks/useCloudbridgeQuillImages';
import RecipientTagList from '@/components/email/RecipientTagList';
import ImageUploadOverlay from '@/components/email/ImageUploadOverlay';
import {
  MAX_RECIPIENTS,
  mergeRecipientsIntoTags,
  shouldParseBulkPaste,
} from '@/utils/recipientParser';
import { toast } from 'react-hot-toast';

interface EmailData {
  to: Tag[];
  cc: Tag[];
  bcc: Tag[];
  subject: string;
  body: string;
  attachments: File[];
}

interface EmailComposerProps {
  type?: 'new' | 'reply' | 'forward' | 'draft';
  initialTo?: Tag[];
  initialCc?: Tag[];
  initialBcc?: Tag[];
  initialSubject?: string;
  initialBody?: string;
  initialAttachments?: File[];
  showCcDefault?: boolean;
  showBccDefault?: boolean;
  isSending?: boolean;
  isSaving?: boolean;
  onSend: (data: EmailData) => void;
  onSave?: (data: EmailData) => void;
  onCancel: (data: EmailData) => void;
}

interface ActiveSignature {
  signature_id: string;
  user_id: string;
  signature: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  user: {
    username: string;
    first_name: string;
    last_name: string;
  };
}

const EmailComposer: React.FC<EmailComposerProps> = ({
  type = 'new',
  initialTo = [],
  initialCc = [],
  initialBcc = [],
  initialSubject = '',
  initialBody = '',
  initialAttachments = [],
  showCcDefault = false,
  showBccDefault = false,
  isSending = false,
  isSaving = false,
  onSend,
  onSave,
  onCancel,
}) => {
  // Form state
  const [toTags, setToTags] = useState<Tag[]>(initialTo);
  const [ccTags, setCcTags] = useState<Tag[]>(initialCc);
  const [bccTags, setBccTags] = useState<Tag[]>(initialBcc);
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const [attachments, setAttachments] = useState<File[]>(initialAttachments);
  const [signature, setSignature] = useState<string>('');

  // UI state
  const [showCc, setShowCc] = useState(showCcDefault);
  const [showBcc, setShowBcc] = useState(showBccDefault);
  const [expandedToTags, setExpandedToTags] = useState(false);
  const [expandedCcTags, setExpandedCcTags] = useState(false);
  const [expandedBccTags, setExpandedBccTags] = useState(false);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isSignatureAppended = useRef(false);
  const {
    quillRef,
    quillModules,
    handleBodyChange,
    releaseTracked,
    discardTracked,
    isUploadingImage,
    uploadProgress,
    uploadFileName,
  } = useCloudbridgeQuillImages();

  // Load active signature and set initial body with signature
  useEffect(() => {
    const storedSignature = localStorage.getItem('activeSignature');
    // Don't append signature for drafts since they already have it
    if (storedSignature && !isSignatureAppended.current && type !== 'draft') {
      try {
        const activeSignature: ActiveSignature = JSON.parse(storedSignature);
        setSignature(activeSignature.signature);
        
        // Only append signature if we haven't done it yet and there's an initial body
        const signatureHtml = `<br><br>${activeSignature.signature}`;
        setBody(initialBody + signatureHtml);
        isSignatureAppended.current = true;
      } catch (error) {
        console.error('Error parsing active signature:', error);
      }
    }
  }, [type]);

  // Watch for signature changes in localStorage
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'activeSignature' && e.newValue) {
        try {
          const activeSignature: ActiveSignature = JSON.parse(e.newValue);
          setSignature(activeSignature.signature);
          
          // Update the body with the new signature
          // First, remove the old signature if it exists
          const bodyWithoutSignature = body.replace(new RegExp(`<br><br>${signature}$`), '');
          setBody(bodyWithoutSignature + `<br><br>${activeSignature.signature}`);
        } catch (error) {
          console.error('Error handling signature change:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [signature, body]);

  // Handle file attachment
  const handleFileAttach = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setAttachments(prev => [...prev, ...files]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove attachment
  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getTotalRecipientCount = (
    currentTags: Tag[],
    otherCounts: number[],
  ) => otherCounts.reduce((sum, count) => sum + count, 0) + currentTags.length;

  const applyRecipientInput = (
    value: string,
    setTags: React.Dispatch<React.SetStateAction<Tag[]>>,
    currentTags: Tag[],
    otherFieldCounts: number[],
  ) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return false;

    const remainingSlots = MAX_RECIPIENTS - getTotalRecipientCount(currentTags, otherFieldCounts);
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${MAX_RECIPIENTS} recipients allowed per email`);
      return false;
    }

    const result = mergeRecipientsIntoTags(currentTags, trimmedValue, remainingSlots);
    if (result.added === 0 && result.invalid.length === 0 && result.duplicates > 0) {
      toast.error('All pasted addresses are already added');
      return false;
    }

    if (result.added > 0) {
      setTags(result.tags);
      if (result.added > 1) {
        toast.success(`Added ${result.added} recipients`);
      }
    }

    if (result.invalid.length > 0) {
      toast.error(`Skipped ${result.invalid.length} invalid address(es)`);
    }

    if (result.duplicates > 0 && result.added > 0) {
      toast(`${result.duplicates} duplicate address(es) skipped`, { icon: 'ℹ️' });
    }

    if (result.limitReached) {
      toast.error(`Maximum ${MAX_RECIPIENTS} recipients allowed per email`);
    }

    return result.added > 0;
  };

  const handleAddEmailTag = (
    value: string,
    setTags: React.Dispatch<React.SetStateAction<Tag[]>>,
    currentTags: Tag[],
    otherFieldCounts: number[],
  ) => applyRecipientInput(value, setTags, currentTags, otherFieldCounts);

  const handleRecipientPaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
    setTags: React.Dispatch<React.SetStateAction<Tag[]>>,
    currentTags: Tag[],
    otherFieldCounts: number[],
  ) => {
    const pasted = event.clipboardData.getData('text');
    if (!shouldParseBulkPaste(pasted)) return;

    event.preventDefault();
    applyRecipientInput(pasted, setTags, currentTags, otherFieldCounts);
    event.currentTarget.value = '';
    setSearchQuery('');
  };

  // Email search state
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [activeInput, setActiveInput] = useState<'to' | 'cc' | 'bcc' | null>(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Fetch email suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (debouncedSearchQuery.length > 0) {
        try {
          const results = await emailAddressSearchService.searchAddresses(debouncedSearchQuery);
          setSuggestions(results);
          setShowSuggestions(true);
          setActiveSuggestionIndex(-1);
        } catch (error) {
          // Only log error if it's not a cancelled request
          if (!(error instanceof Error && error.name === 'AbortError')) {
            console.error('Error fetching email suggestions:', error);
          }
          setSuggestions([]);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    fetchSuggestions();

    // Cleanup function to cancel any pending requests when component unmounts
    // or when search query changes
    return () => {
      emailAddressSearchService.cancelPreviousSearch();
    };
  }, [debouncedSearchQuery]);

  // Handle suggestion selection
  const handleSelectSuggestion = (email: string) => {
    const newTag = {
      label: email,
      value: email
    };

    switch (activeInput) {
      case 'to':
        setToTags([...toTags, newTag]);
        break;
      case 'cc':
        setCcTags([...ccTags, newTag]);
        break;
      case 'bcc':
        setBccTags([...bccTags, newTag]);
        break;
    }

    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, inputType: 'to' | 'cc' | 'bcc') => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveSuggestionIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (activeSuggestionIndex >= 0) {
          handleSelectSuggestion(suggestions[activeSuggestionIndex]);
          if (e.currentTarget) {
            e.currentTarget.value = '';
          }
        } else if (e.currentTarget.value.trim()) {
          handleAddEmailTag(
            e.currentTarget.value,
            inputType === 'to' ? setToTags :
            inputType === 'cc' ? setCcTags :
            setBccTags,
            inputType === 'to' ? toTags :
            inputType === 'cc' ? ccTags :
            bccTags,
            inputType === 'to' ? [ccTags.length, bccTags.length] :
            inputType === 'cc' ? [toTags.length, bccTags.length] :
            [toTags.length, ccTags.length],
          );
          e.currentTarget.value = '';
          setSearchQuery('');
        }
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
        setActiveSuggestionIndex(-1);
      }
    } else if (e.key === 'Enter' && e.currentTarget.value.trim()) {
      e.preventDefault();
      handleAddEmailTag(
        e.currentTarget.value,
        inputType === 'to' ? setToTags :
        inputType === 'cc' ? setCcTags :
        setBccTags,
        inputType === 'to' ? toTags :
        inputType === 'cc' ? ccTags :
        bccTags,
        inputType === 'to' ? [ccTags.length, bccTags.length] :
        inputType === 'cc' ? [toTags.length, bccTags.length] :
        [toTags.length, ccTags.length],
      );
      e.currentTarget.value = '';
      setSearchQuery('');
    }
  };

  // Custom CSS for ReactQuill styling
  const quillStyles = `
    .ql-container {
      border: none !important;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.6;
      height: calc(100% - 42px) !important; /* 42px is toolbar height */
    }
    .ql-editor {
      height: 100% !important;
      max-height: 400px !important;
      padding: 16px !important;
      border: none !important;
      outline: none !important;
      overflow-y: auto !important;
    }
    .ql-toolbar {
      border: none !important;
      border-bottom: 1px solid #e5e7eb !important;
      background-color: #f9fafb;
      padding: 8px 16px !important;
      border-radius: 8px 8px 0 0;
      position: sticky;
      top: 0;
      z-index: 10;
    }
    .ql-toolbar button {
      border-radius: 4px !important;
      margin: 2px !important;
    }
    .ql-toolbar button:hover {
      background-color: #e5e7eb !important;
    }
    .ql-toolbar .ql-active {
      background-color: #dbeafe !important;
      color: #1e40af !important;
    }
    .ql-container.ql-snow {
      border: none !important;
      border-radius: 0 0 8px 8px;
    }
    .ql-toolbar.ql-snow {
      border: none !important;
    }
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  // Custom CSS for ReactQuill styling and mobile responsiveness
  const styles = `
    .email-composer {
      display: flex;
      flex-direction: column;
      gap: 0px;
      min-height: 0;
      flex: 1;
    }

    .email-composer-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 24px;
      border-bottom: 1px solid #e5e7eb;
      background-color: #ffffff;
    }

    .email-composer-content {
      flex: 1;
      overflow: auto;
      padding: 24px;
    }

    .email-composer-footer {
      position: sticky;
      bottom: 0;
      left: 0;
      right: 0;
      background: #fff;
      border-top: 1px solid #e5e7eb;
      padding: 16px 24px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      z-index: 20;
    }

    /* Mobile styles */
    @media (max-width: 768px) {
      body.modal-open {
        overflow: hidden;
        position: fixed;
        width: 100%;
        height: 100%;
      }

      .email-composer {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        z-index: 9999 !important;
        background-color: #ffffff !important;
        gap: 0;
        height: 100vh !important;
        width: 100vw !important;
        overflow: hidden !important;
        transform: none !important;
        opacity: 1 !important;
        visibility: visible !important;
        transition: none !important;
        animation: none !important;
        border-radius: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        display: flex !important;
        flex-direction: column !important;
      }

      .email-composer-header {
        position: sticky;
        top: 0;
        z-index: 20;
        padding: 12px 16px;
        flex-shrink: 0;
      }

      .email-composer-content {
        flex: 1;
        padding: 16px;
        padding-bottom: 180px;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        height: calc(100vh - 120px);
      }

      .email-composer-footer {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 12px 16px;
        box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
        background: #ffffff;
        z-index: 30;
      }

      .email-composer-button {
        padding: 8px 12px !important;
      }

      .email-composer-title {
        font-size: 16px !important;
      }

      /* Attachment section mobile styles */
      .attachment-section {
        margin-top: 16px !important;
        margin-bottom: 80px !important;
        padding: 0 !important;
      }

      .attachment-list {
        max-height: 200px !important;
        border-radius: 8px !important;
        overflow-y: auto !important;
        -webkit-overflow-scrolling: touch !important;
      }

      .attachment-item {
        padding: 12px !important;
        margin-bottom: 8px !important;
      }

      .attachment-item:last-child {
        margin-bottom: 0 !important;
      }

      /* Ensure the Quill editor doesn't overflow */
      .ql-container {
        max-height: calc(100vh - 400px) !important;
        overflow-y: auto !important;
      }

      .ql-editor {
        min-height: 200px !important;
        max-height: none !important;
      }
    }

    ${quillStyles}
  `;

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);

    // Add class to body on mobile
    if (window.innerWidth <= 768) {
      document.body.classList.add('modal-open');
    }

    return () => {
      document.head.removeChild(styleElement);
      document.body.classList.remove('modal-open');
    };
  }, [styles]);

  // Get current email data
  const getCurrentData = (): EmailData => ({
    to: toTags,
    cc: ccTags,
    bcc: bccTags,
    subject,
    body,
    attachments
  });

  // Handle send
  const handleSend = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    releaseTracked();
    onSend(getCurrentData());
  };

  // Handle save
  const handleSave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onSave?.(getCurrentData());
  };

  // Handle cancel
  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    discardTracked();
    onCancel(getCurrentData());
  };

  return (
    <div className="email-composer">
      {/* Header Section */}
      <div className="email-composer-header">
        <h2 className="email-composer-title" style={{ fontSize: '18px', fontWeight: '600' }}>New Message</h2>
        <button 
          onClick={handleCancel}
          style={{ 
            padding: '8px', 
            borderRadius: '6px',
            backgroundColor: 'transparent',
            color: '#6b7280',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Main Content Area */}
      <div className="email-composer-content">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Recipients Section */}
          <div style={{ 
            paddingTop: '0px', 
            paddingLeft: '0px', 
            paddingRight: '0px', 
            overflow: 'visible',
            marginBottom: '-24px'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* To Field */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', position: 'relative' }}>
                <label style={{ 
                  fontWeight: '600', 
                  color: '#374151', 
                  fontSize: '14px', 
                  minWidth: '32px',
                  marginTop: '8px'
                }}>To:</label>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '4px', 
                  flex: 1, 
                  minHeight: '24px', 
                  alignItems: 'flex-start',
                  borderBottom: '1px solid #d1d5db', 
                  borderRadius: '0px', 
                  padding: '8px 12px', 
                  backgroundColor: '#ffffff', 
                  transition: 'all 0.2s ease',
                  overflow: 'visible',
                  position: 'relative'
                }}>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px',
                    maxWidth: '100%'
                  }}>
                    <RecipientTagList
                      tags={toTags}
                      expanded={expandedToTags}
                      onToggleExpand={() => setExpandedToTags((value) => !value)}
                      onRemove={(index) => setToTags(toTags.filter((_, idx) => idx !== index))}
                      onClearAll={() => {
                        setToTags([]);
                        setExpandedToTags(false);
                      }}
                    />
                  </div>
                  <input 
                    type="text" 
                    placeholder="Add recipient" 
                    style={{ 
                      border: 'none',
                      outline: 'none',
                      fontSize: '12px',
                      height: '24px',
                      minWidth: '120px',
                      backgroundColor: 'transparent',
                      color: '#374151',
                      flex: 1
                    }}
                    value={activeInput === 'to' ? searchQuery : ''}
                    onChange={e => {
                      setSearchQuery(e.target.value);
                      setActiveInput('to');
                    }}
                    onKeyDown={e => handleKeyDown(e, 'to')}
                    onPaste={(e) => handleRecipientPaste(e, setToTags, toTags, [ccTags.length, bccTags.length])}
                    onBlur={e => {
                      // Delay hiding suggestions to allow clicking them
                      setTimeout(() => {
                        setShowSuggestions(false);
                        if (handleAddEmailTag(e.target.value, setToTags, toTags, [ccTags.length, bccTags.length])) {
                          setSearchQuery('');
                        }
                      }, 200);
                    }}
                    onFocus={() => setActiveInput('to')}
                  />
                  {activeInput === 'to' && showSuggestions && suggestions.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      zIndex: 50
                    }}>
                      {suggestions.map((suggestion, index) => (
                        <div
                          key={suggestion}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            backgroundColor: index === activeSuggestionIndex ? '#f3f4f6' : 'transparent',
                            color: '#374151',
                            fontSize: '14px'
                          }}
                          onMouseEnter={() => setActiveSuggestionIndex(index)}
                          onMouseDown={() => handleSelectSuggestion(suggestion)}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '4px', marginTop: '8px' }}>
                  <button
                    style={{
                      padding: '4px 8px',
                      borderRadius: '6px',
                      backgroundColor: '#f3f4f6',
                      color: '#4b5563',
                      fontSize: '12px',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onClick={() => setShowCc(v => !v)}
                  >CC</button>
                  <button
                    style={{
                      padding: '4px 8px',
                      borderRadius: '6px',
                      backgroundColor: '#f3f4f6',
                      color: '#4b5563',
                      fontSize: '12px',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onClick={() => setShowBcc(v => !v)}
                  >BCC</button>
                </div>
              </div>

              {/* CC Field */}
              {showCc && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', position: 'relative' }}>
                  <label style={{ 
                    fontWeight: '600', 
                    color: '#374151', 
                    fontSize: '14px', 
                    minWidth: '32px',
                    marginTop: '8px'
                  }}>Cc:</label>
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '4px', 
                    flex: 1, 
                    minHeight: '24px', 
                    alignItems: 'flex-start',
                    borderBottom: '1px solid #d1d5db', 
                    borderRadius: '0px', 
                    padding: '8px 12px', 
                    backgroundColor: '#ffffff', 
                    transition: 'all 0.2s ease',
                    overflow: 'visible',
                    position: 'relative'
                  }}>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px',
                      maxWidth: '100%'
                    }}>
                      <RecipientTagList
                        tags={ccTags}
                        expanded={expandedCcTags}
                        onToggleExpand={() => setExpandedCcTags((value) => !value)}
                        onRemove={(index) => setCcTags(ccTags.filter((_, idx) => idx !== index))}
                        onClearAll={() => {
                          setCcTags([]);
                          setExpandedCcTags(false);
                        }}
                      />
                    </div>
                    <input 
                      type="text" 
                      placeholder="Add CC" 
                      style={{ 
                        border: 'none',
                        outline: 'none',
                        fontSize: '12px',
                        height: '24px',
                        minWidth: '120px',
                        backgroundColor: 'transparent',
                        color: '#374151',
                        flex: 1
                      }}
                      value={activeInput === 'cc' ? searchQuery : ''}
                      onChange={e => {
                        setSearchQuery(e.target.value);
                        setActiveInput('cc');
                      }}
                      onKeyDown={e => handleKeyDown(e, 'cc')}
                      onPaste={(e) => handleRecipientPaste(e, setCcTags, ccTags, [toTags.length, bccTags.length])}
                      onBlur={e => {
                        setTimeout(() => {
                          setShowSuggestions(false);
                          if (handleAddEmailTag(e.target.value, setCcTags, ccTags, [toTags.length, bccTags.length])) {
                            setSearchQuery('');
                          }
                        }, 200);
                      }}
                      onFocus={() => setActiveInput('cc')}
                    />
                    {activeInput === 'cc' && showSuggestions && suggestions.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        zIndex: 50
                      }}>
                        {suggestions.map((suggestion, index) => (
                          <div
                            key={suggestion}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              backgroundColor: index === activeSuggestionIndex ? '#f3f4f6' : 'transparent',
                              color: '#374151',
                              fontSize: '14px'
                            }}
                            onMouseEnter={() => setActiveSuggestionIndex(index)}
                            onMouseDown={() => handleSelectSuggestion(suggestion)}
                          >
                            {suggestion}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* BCC Field */}
              {showBcc && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', position: 'relative' }}>
                  <label style={{ 
                    fontWeight: '600', 
                    color: '#374151', 
                    fontSize: '14px', 
                    minWidth: '32px',
                    marginTop: '8px'
                  }}>Bcc:</label>
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '4px', 
                    flex: 1, 
                    minHeight: '24px', 
                    alignItems: 'flex-start',
                    borderBottom: '1px solid #d1d5db', 
                    borderRadius: '0px', 
                    padding: '8px 12px', 
                    backgroundColor: '#ffffff', 
                    transition: 'all 0.2s ease',
                    overflow: 'visible',
                    position: 'relative'
                  }}>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px',
                      maxWidth: '100%'
                    }}>
                      <RecipientTagList
                        tags={bccTags}
                        expanded={expandedBccTags}
                        onToggleExpand={() => setExpandedBccTags((value) => !value)}
                        onRemove={(index) => setBccTags(bccTags.filter((_, idx) => idx !== index))}
                        onClearAll={() => {
                          setBccTags([]);
                          setExpandedBccTags(false);
                        }}
                      />
                    </div>
                    <input 
                      type="text" 
                      placeholder="Add BCC" 
                      style={{ 
                        border: 'none',
                        outline: 'none',
                        fontSize: '12px',
                        height: '24px',
                        minWidth: '120px',
                        backgroundColor: 'transparent',
                        color: '#374151',
                        flex: 1
                      }}
                      value={activeInput === 'bcc' ? searchQuery : ''}
                      onChange={e => {
                        setSearchQuery(e.target.value);
                        setActiveInput('bcc');
                      }}
                      onKeyDown={e => handleKeyDown(e, 'bcc')}
                      onPaste={(e) => handleRecipientPaste(e, setBccTags, bccTags, [toTags.length, ccTags.length])}
                      onBlur={e => {
                        setTimeout(() => {
                          setShowSuggestions(false);
                          if (handleAddEmailTag(e.target.value, setBccTags, bccTags, [toTags.length, ccTags.length])) {
                            setSearchQuery('');
                          }
                        }, 200);
                      }}
                      onFocus={() => setActiveInput('bcc')}
                    />
                    {activeInput === 'bcc' && showSuggestions && suggestions.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        zIndex: 50
                      }}>
                        {suggestions.map((suggestion, index) => (
                          <div
                            key={suggestion}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              backgroundColor: index === activeSuggestionIndex ? '#f3f4f6' : 'transparent',
                              color: '#374151',
                              fontSize: '14px'
                            }}
                            onMouseEnter={() => setActiveSuggestionIndex(index)}
                            onMouseDown={() => handleSelectSuggestion(suggestion)}
                          >
                            {suggestion}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Subject */}
              <div style={{ marginTop: '8px' }}>
                <label style={{ fontWeight: '600', color: '#374151', fontSize: '14px', display: 'block', marginBottom: '8px' }}>Subject:</label>
                <input type="text" value={subject} onChange={e => setSubject(e.target.value)} style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '12px 16px', fontSize: '14px', fontWeight: '600', outline: 'none', transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)', height: '40px' }} onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'; }} onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }} placeholder="Subject" />
              </div>

              {/* Message Section */}
              <div style={{ marginTop: '0px' }}>
                <label style={{ fontWeight: '600', color: '#374151', fontSize: '14px', display: 'block', marginBottom: '8px' }}>Message:</label>
                <div style={{ 
                  border: '1px solid #d1d5db', 
                  borderRadius: '8px',
                  height: '450px',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease', 
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                }}>
                  <ImageUploadOverlay
                    active={isUploadingImage}
                    progress={uploadProgress}
                    fileName={uploadFileName}
                  />
                  <ReactQuill 
                    ref={quillRef}
                    value={body} 
                    onChange={(value) => handleBodyChange(value, setBody)} 
                    theme="snow" 
                    style={{ 
                      height: '100%',
                      border: 'none', 
                      outline: 'none'
                    }} 
                    modules={quillModules}
                  />
                </div>
              </div>

              {/* Attachment Section */}
              <div className="attachment-section" style={{ marginTop: '24px', marginBottom: '24px' }}>
                <label style={{ fontWeight: '600', color: '#374151', fontSize: '14px', display: 'block', marginBottom: '8px' }}>Attachments:</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <button 
                    type="button" 
                    onClick={() => fileInputRef.current?.click()} 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      padding: '8px 16px', 
                      backgroundColor: '#f3f4f6', 
                      color: '#374151', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '6px', 
                      cursor: 'pointer', 
                      fontSize: '14px', 
                      fontWeight: '500', 
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap'
                    }} 
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e5e7eb'} 
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  >
                    <Paperclip size={16} />Attach Files
                  </button>
                  <input ref={fileInputRef} type="file" multiple onChange={handleFileAttach} style={{ display: 'none' }} accept="*/*" />
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>{attachments.length} file(s) attached</span>
                </div>
                {attachments.length > 0 && (
                  <div className="attachment-list" style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '8px', 
                    maxHeight: '200px', 
                    overflowY: 'auto', 
                    padding: '12px', 
                    backgroundColor: '#f9fafb', 
                    borderRadius: '6px', 
                    border: '1px solid #e5e7eb',
                    WebkitOverflowScrolling: 'touch'
                  }}>
                    {attachments.map((file, index) => (
                      <div 
                        key={index} 
                        className="attachment-item"
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between', 
                          padding: '8px 12px', 
                          backgroundColor: '#ffffff', 
                          borderRadius: '4px', 
                          border: '1px solid #e5e7eb'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                          <FileText size={16} color="#6b7280" />
                          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                            <span style={{ 
                              fontSize: '14px', 
                              fontWeight: '500', 
                              color: '#374151', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap' 
                            }}>
                              {file.name}
                            </span>
                            <span style={{ fontSize: '12px', color: '#6b7280' }}>
                              {formatFileSize(file.size)}
                            </span>
                          </div>
                        </div>
                        <button 
                          onClick={() => removeAttachment(index)} 
                          style={{ 
                            background: 'none', 
                            border: 'none', 
                            color: '#6b7280', 
                            cursor: 'pointer', 
                            padding: '8px', // Increased touch target
                            borderRadius: '4px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            transition: 'all 0.2s ease',
                            marginLeft: '8px'
                          }} 
                          onMouseEnter={e => { 
                            e.currentTarget.style.color = '#dc2626'; 
                            e.currentTarget.style.backgroundColor = '#fef2f2'; 
                          }} 
                          onMouseLeave={e => { 
                            e.currentTarget.style.color = '#6b7280'; 
                            e.currentTarget.style.backgroundColor = 'transparent'; 
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons Fixed */}
      <div className="email-composer-footer">
        <div style={{ display: 'flex', gap: '8px' }}>
          <button 
            onClick={handleCancel} 
            className="email-composer-button"
            style={{ 
              padding: '8px 16px', 
              borderRadius: '8px', 
              backgroundColor: '#f3f4f6', 
              color: '#374151', 
              fontWeight: '500', 
              border: '1px solid #d1d5db', 
              cursor: 'pointer', 
              transition: 'all 0.2s ease', 
              fontSize: '14px'
            }} 
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e5e7eb'} 
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          >
            Cancel
          </button>
          {onSave && (
            <button 
              onClick={handleSave}
              disabled={isSaving}
              className="email-composer-button"
              style={{ 
                padding: '8px 16px', 
                borderRadius: '8px', 
                backgroundColor: '#f3f4f6',
                color: '#374151', 
                fontWeight: '500', 
                border: '1px solid #d1d5db',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: isSaving ? 0.7 : 1,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={e => { if (!isSaving) { e.currentTarget.style.backgroundColor = '#e5e7eb'; } }}
              onMouseLeave={e => { if (!isSaving) { e.currentTarget.style.backgroundColor = '#f3f4f6'; } }}
            >
              {isSaving ? (
                <>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Saving...
                </>
              ) : (
                'Save Draft'
              )}
            </button>
          )}
        </div>
        <button 
          onClick={handleSend} 
          disabled={isSending} 
          className="email-composer-button"
          style={{ 
            padding: '8px 16px', 
            borderRadius: '8px', 
            backgroundColor: isSending ? '#9ca3af' : '#8D00E4', 
            color: '#ffffff', 
            fontWeight: '500', 
            border: 'none', 
            cursor: isSending ? 'not-allowed' : 'pointer', 
            fontSize: '14px', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', 
            opacity: isSending ? 0.7 : 1, 
            transition: 'all 0.2s ease'
          }} 
          onMouseEnter={e => { if (!isSending) { e.currentTarget.style.backgroundColor = '#7c00d1'; } }} 
          onMouseLeave={e => { if (!isSending) { e.currentTarget.style.backgroundColor = '#8D00E4'; } }}
        >
          {isSending ? (
            <>
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
              Sending...
            </>
          ) : (
            'Send'
          )}
        </button>
      </div>
    </div>
  );
};

export default EmailComposer; 